/**
 * ConnectorWizard — wizard 3 step per configurare una nuova sorgente API.
 *
 * Step 1: Seleziona il tipo di connettore (Google Sheets, Stripe, Float)
 * Step 2: Inserisci le credenziali/configurazione specifica
 * Step 3: Riepilogo e conferma creazione
 */

import { useState } from 'react'
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Clock,
  CreditCard,
  ExternalLink,
  RefreshCw,
  Sheet,
  X,
} from 'lucide-react'
import { clsx } from 'clsx'
import { useCreateSource, type DataSource } from '../../hooks/useSources'
import { AlertBanner } from '../ui/AlertBanner'
import { LimitExceededBanner } from '../ui/LimitExceededBanner'
import { LimitExceededError } from '../../lib/api'

// ─────────────────────────────────────────────────────────────────────────────
// Connector definitions
// ─────────────────────────────────────────────────────────────────────────────

interface ConfigField {
  key: string
  label: string
  type: 'text' | 'password' | 'url'
  required: boolean
  placeholder: string
  hint?: string
}

interface ConnectorDef {
  id: string
  name: string
  description: string
  icon: React.ElementType
  iconColor: string
  fields: ConfigField[]
  docsUrl?: string
}

const CONNECTORS: ConnectorDef[] = [
  {
    id: 'google_sheets',
    name: 'Google Sheets',
    description: 'Importa dati da un foglio Google Sheets condiviso tramite URL o ID.',
    icon: Sheet,
    iconColor: 'text-emerald-500',
    docsUrl: 'https://support.google.com/docs/answer/37579',
    fields: [
      {
        key: 'sheet_id',
        label: 'URL o ID del foglio',
        type: 'url',
        required: true,
        placeholder: 'https://docs.google.com/spreadsheets/d/...',
        hint: "Incolla l'URL del foglio oppure solo l'ID (la stringa lunga nell'URL).",
      },
      {
        key: 'range',
        label: 'Intervallo celle (opzionale)',
        type: 'text',
        required: false,
        placeholder: 'Foglio1!A1:Z1000',
        hint: 'Lascia vuoto per importare tutto il primo foglio.',
      },
    ],
  },
  {
    id: 'stripe',
    name: 'Stripe',
    description: 'Sincronizza fatture e pagamenti direttamente da Stripe.',
    icon: CreditCard,
    iconColor: 'text-indigo-500',
    docsUrl: 'https://dashboard.stripe.com/apikeys',
    fields: [
      {
        key: 'api_key',
        label: 'Secret Key',
        type: 'password',
        required: true,
        placeholder: 'sk_live_...',
        hint: 'Usa una chiave con permessi di lettura (Restricted Key). Non condividere mai questa chiave.',
      },
    ],
  },
  {
    id: 'float',
    name: 'Float',
    description: 'Importa le ore di produzione e la pianificazione risorse da Float.com.',
    icon: Clock,
    iconColor: 'text-amber-500',
    docsUrl: 'https://support.float.com/en/articles/4482581',
    fields: [
      {
        key: 'api_token',
        label: 'API Token',
        type: 'password',
        required: true,
        placeholder: 'Il tuo Float API token',
        hint: 'Trovi il token in Float → Impostazioni Account → Sviluppatori.',
      },
    ],
  },
]

// ─────────────────────────────────────────────────────────────────────────────
// Step indicator
// ─────────────────────────────────────────────────────────────────────────────

const STEP_LABELS = ['Sorgente', 'Configurazione', 'Conferma']

function StepIndicator({ current }: { current: number }) {
  return (
    <ol className="flex items-center gap-0" aria-label="Avanzamento wizard">
      {STEP_LABELS.map((label, idx) => {
        const step = idx + 1
        const done = current > step
        const active = current === step
        return (
          <li key={label} className="flex items-center">
            <div className="flex items-center gap-2">
              <span
                className={clsx(
                  'flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold shrink-0',
                  done && 'bg-emerald-500 text-white',
                  active && 'bg-[--km-primary] text-white',
                  !done && !active && 'bg-[--km-surface-3] text-[--km-text-muted]',
                )}
                aria-current={active ? 'step' : undefined}
              >
                {done ? <Check size={12} /> : step}
              </span>
              <span
                className={clsx(
                  'text-xs font-medium hidden sm:block',
                  active ? 'text-[--km-text-primary]' : 'text-[--km-text-muted]',
                )}
              >
                {label}
              </span>
            </div>
            {idx < STEP_LABELS.length - 1 && (
              <span className="mx-3 h-px w-8 bg-[--km-border]" aria-hidden="true" />
            )}
          </li>
        )
      })}
    </ol>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Step 1 — Seleziona tipo connettore
// ─────────────────────────────────────────────────────────────────────────────

function Step1Select({
  selected,
  onSelect,
}: {
  selected: ConnectorDef | null
  onSelect: (c: ConnectorDef) => void
}) {
  return (
    <div className="space-y-3">
      <p className="text-sm text-[--km-text-secondary]">
        Scegli la sorgente da connettere. Il sistema sincronizzerà automaticamente i dati nel canonical schema Koremargin.
      </p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3" role="list" aria-label="Tipi di connettore">
        {CONNECTORS.map((c) => {
          const Icon = c.icon
          const isSelected = selected?.id === c.id
          return (
            <button
              key={c.id}
              role="listitem"
              onClick={() => onSelect(c)}
              aria-pressed={isSelected}
              className={clsx(
                'flex flex-col gap-3 rounded-xl border p-5 text-left transition-all duration-150',
                isSelected
                  ? 'border-[--km-primary] bg-[--km-primary]/5 ring-1 ring-[--km-primary]/30'
                  : 'border-[--km-border] bg-[--km-surface] hover:border-[--km-border-hover]',
              )}
            >
              <div
                className={clsx(
                  'flex h-10 w-10 items-center justify-center rounded-lg',
                  isSelected ? 'bg-[--km-primary]/15' : 'bg-[--km-surface-3]',
                )}
              >
                <Icon size={20} className={c.iconColor} aria-hidden="true" />
              </div>
              <div>
                <p className="text-sm font-semibold text-[--km-text-primary]">{c.name}</p>
                <p className="mt-0.5 text-xs text-[--km-text-muted] leading-relaxed">{c.description}</p>
              </div>
              {isSelected && (
                <span className="mt-auto flex items-center gap-1 text-xs font-medium text-[--km-primary]">
                  <Check size={12} />
                  Selezionato
                </span>
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Step 2 — Configura credenziali
// ─────────────────────────────────────────────────────────────────────────────

function Step2Config({
  connector,
  name,
  onNameChange,
  config,
  onConfigChange,
}: {
  connector: ConnectorDef
  name: string
  onNameChange: (v: string) => void
  config: Record<string, string>
  onConfigChange: (key: string, value: string) => void
}) {
  const Icon = connector.icon
  return (
    <div className="space-y-5">
      {/* Connector header */}
      <div className="flex items-center gap-3 rounded-lg border border-[--km-border] bg-[--km-surface-2] p-4">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[--km-surface-3]">
          <Icon size={18} className={connector.iconColor} aria-hidden="true" />
        </div>
        <div>
          <p className="text-sm font-semibold text-[--km-text-primary]">{connector.name}</p>
          <p className="text-xs text-[--km-text-muted]">{connector.description}</p>
        </div>
        {connector.docsUrl && (
          <a
            href={connector.docsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="ml-auto flex items-center gap-1 text-xs text-[--km-primary] hover:underline"
            aria-label={`Documentazione ${connector.name}`}
          >
            Docs <ExternalLink size={11} />
          </a>
        )}
      </div>

      {/* Nome sorgente */}
      <div>
        <label htmlFor="source-name" className="block text-sm font-medium text-[--km-text-primary] mb-1.5">
          Nome sorgente <span className="text-[--km-error]">*</span>
        </label>
        <input
          id="source-name"
          type="text"
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          placeholder={`Es. ${connector.name} - Produzione`}
          className={clsx(
            'w-full rounded-lg border border-[--km-border] bg-[--km-surface] px-3 py-2 text-sm',
            'text-[--km-text-primary] placeholder:text-[--km-text-muted]',
            'focus:outline-none focus:ring-2 focus:ring-[--km-primary]/40 focus:border-[--km-primary]',
            'transition-colors',
          )}
        />
        <p className="mt-1 text-xs text-[--km-text-muted]">Nome visualizzato nella lista sorgenti.</p>
      </div>

      {/* Campi dinamici del connettore */}
      {connector.fields.map((field) => (
        <div key={field.key}>
          <label
            htmlFor={`field-${field.key}`}
            className="block text-sm font-medium text-[--km-text-primary] mb-1.5"
          >
            {field.label}
            {field.required && <span className="text-[--km-error] ml-0.5">*</span>}
          </label>
          <input
            id={`field-${field.key}`}
            type={field.type}
            value={config[field.key] ?? ''}
            onChange={(e) => onConfigChange(field.key, e.target.value)}
            placeholder={field.placeholder}
            autoComplete={field.type === 'password' ? 'new-password' : undefined}
            className={clsx(
              'w-full rounded-lg border border-[--km-border] bg-[--km-surface] px-3 py-2 text-sm',
              'text-[--km-text-primary] placeholder:text-[--km-text-muted]',
              'focus:outline-none focus:ring-2 focus:ring-[--km-primary]/40 focus:border-[--km-primary]',
              'transition-colors',
            )}
          />
          {field.hint && (
            <p className="mt-1 text-xs text-[--km-text-muted]">{field.hint}</p>
          )}
        </div>
      ))}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Step 3 — Riepilogo e conferma
// ─────────────────────────────────────────────────────────────────────────────

function Step3Review({
  connector,
  name,
  config,
}: {
  connector: ConnectorDef
  name: string
  config: Record<string, string>
}) {
  const Icon = connector.icon
  return (
    <div className="space-y-5">
      <p className="text-sm text-[--km-text-secondary]">
        Verifica le impostazioni prima di creare la sorgente. Le credenziali vengono trasmesse in modo sicuro e non vengono mai memorizzate in chiaro.
      </p>

      <div className="rounded-xl border border-[--km-border] bg-[--km-surface] divide-y divide-[--km-border]">
        {/* Tipo connettore */}
        <div className="flex items-center gap-4 px-5 py-4">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[--km-surface-3]">
            <Icon size={18} className={connector.iconColor} aria-hidden="true" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-[--km-text-muted]">Tipo connettore</p>
            <p className="text-sm font-semibold text-[--km-text-primary]">{connector.name}</p>
          </div>
        </div>

        {/* Nome */}
        <div className="px-5 py-4">
          <p className="text-xs text-[--km-text-muted]">Nome sorgente</p>
          <p className="text-sm font-medium text-[--km-text-primary] mt-0.5">{name}</p>
        </div>

        {/* Campi config (oscura password) */}
        {connector.fields.map((field) => {
          const val = config[field.key]
          if (!val) return null
          const displayed =
            field.type === 'password'
              ? val.slice(0, 6) + '••••••••' + val.slice(-3)
              : val.length > 60 ? val.slice(0, 60) + '…' : val
          return (
            <div key={field.key} className="px-5 py-4">
              <p className="text-xs text-[--km-text-muted]">{field.label}</p>
              <p className="text-sm font-medium text-[--km-text-primary] mt-0.5 font-mono break-all">
                {displayed}
              </p>
            </div>
          )
        })}
      </div>

      <AlertBanner
        severity="info"
        message="Dopo la creazione potrai avviare la prima sincronizzazione manuale dalla pagina Sorgenti."
      />
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// ConnectorWizard
// ─────────────────────────────────────────────────────────────────────────────

export interface ConnectorWizardProps {
  onClose: () => void
  onSuccess: (source: DataSource) => void
}

export function ConnectorWizard({ onClose, onSuccess }: ConnectorWizardProps) {
  const [step, setStep] = useState<1 | 2 | 3>(1)
  const [selectedConnector, setSelectedConnector] = useState<ConnectorDef | null>(null)
  const [name, setName] = useState('')
  const [config, setConfig] = useState<Record<string, string>>({})

  const createSource = useCreateSource()

  // ── validation ──────────────────────────────────────────────────────────

  const step1Valid = selectedConnector !== null

  const step2Valid =
    selectedConnector !== null &&
    name.trim().length > 0 &&
    selectedConnector.fields
      .filter((f) => f.required)
      .every((f) => (config[f.key] ?? '').trim().length > 0)

  // ── handlers ────────────────────────────────────────────────────────────

  function handleSelectConnector(c: ConnectorDef) {
    setSelectedConnector(c)
    // Reset config when type changes
    setConfig({})
    setName('')
  }

  function handleConfigChange(key: string, value: string) {
    setConfig((prev) => ({ ...prev, [key]: value }))
  }

  async function handleCreate() {
    if (!selectedConnector || !step2Valid) return
    try {
      const source = await createSource.mutateAsync({
        name: name.trim(),
        source_type: selectedConnector.id,
        config,
      })
      onSuccess(source)
    } catch {
      // error shown via createSource.error
    }
  }

  // ── render ───────────────────────────────────────────────────────────────

  return (
    <section aria-label="Aggiungi sorgente dati" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-[--km-text-primary]">Aggiungi sorgente</h2>
          <p className="text-sm text-[--km-text-muted] mt-0.5">
            Connetti una nuova sorgente dati al tuo account.
          </p>
        </div>
        <button
          onClick={onClose}
          className="p-2 rounded-lg text-[--km-text-muted] hover:text-[--km-text-primary] hover:bg-[--km-surface-2] transition-colors"
          aria-label="Chiudi wizard"
        >
          <X size={18} />
        </button>
      </div>

      {/* Step indicator */}
      <div className="rounded-xl border border-[--km-border] bg-[--km-surface] p-5">
        <StepIndicator current={step} />
      </div>

      {/* Step content */}
      <div className="rounded-xl border border-[--km-border] bg-[--km-surface] p-6">
        {step === 1 && (
          <Step1Select
            selected={selectedConnector}
            onSelect={handleSelectConnector}
          />
        )}
        {step === 2 && selectedConnector && (
          <Step2Config
            connector={selectedConnector}
            name={name}
            onNameChange={setName}
            config={config}
            onConfigChange={handleConfigChange}
          />
        )}
        {step === 3 && selectedConnector && (
          <Step3Review
            connector={selectedConnector}
            name={name}
            config={config}
          />
        )}
      </div>

      {/* Error */}
      <LimitExceededBanner error={createSource.error} />
      {createSource.error && !(createSource.error instanceof LimitExceededError) && (
        <AlertBanner
          severity="critical"
          message={`Impossibile creare la sorgente: ${createSource.error.message}`}
        />
      )}

      {/* Footer navigation */}
      <div className="flex items-center justify-between">
        <button
          onClick={step === 1 ? onClose : () => setStep((s) => (s - 1) as 1 | 2 | 3)}
          className="flex items-center gap-2 rounded-lg border border-[--km-border] px-4 py-2 text-sm font-medium text-[--km-text-secondary] transition-colors hover:border-[--km-border-hover] hover:text-[--km-text-primary]"
        >
          {step === 1 ? (
            <>
              <X size={14} />
              Annulla
            </>
          ) : (
            <>
              <ArrowLeft size={14} />
              Indietro
            </>
          )}
        </button>

        {step < 3 ? (
          <button
            onClick={() => setStep((s) => (s + 1) as 2 | 3)}
            disabled={step === 1 ? !step1Valid : !step2Valid}
            className={clsx(
              'flex items-center gap-2 rounded-lg bg-[--km-primary] px-5 py-2 text-sm font-medium text-white transition-opacity',
              (step === 1 ? !step1Valid : !step2Valid)
                ? 'opacity-40 cursor-not-allowed'
                : 'hover:opacity-90',
            )}
          >
            Avanti
            <ArrowRight size={14} />
          </button>
        ) : (
          <button
            onClick={handleCreate}
            disabled={createSource.isPending}
            className={clsx(
              'flex items-center gap-2 rounded-lg bg-[--km-primary] px-5 py-2 text-sm font-medium text-white transition-opacity',
              createSource.isPending ? 'opacity-60 cursor-not-allowed' : 'hover:opacity-90',
            )}
          >
            {createSource.isPending ? (
              <RefreshCw size={14} className="animate-spin" aria-hidden="true" />
            ) : (
              <Check size={14} aria-hidden="true" />
            )}
            {createSource.isPending ? 'Creazione…' : 'Crea sorgente'}
          </button>
        )}
      </div>
    </section>
  )
}
